import React from "react";
import { TimeEdit } from "../lib/TimeEdit";
import API from "../lib/TimeEditAPI";
import Language from "../lib/Language";
import Log from "../lib/Log";
import ArrayInput from "./ArrayInput";
import MultiSelect from "./MultiSelect";
import RangeInput from "./RangeInput";
import _ from "underscore";
import ReservationStatus, { Status } from "../lib/ReservationStatus";
import { TAppContextProp, withAppContext } from "../contexts/AppContext";
import { LDFlagSet } from "launchdarkly-react-client-sdk";
import { User } from "../models/User";

const LISTABLE_STATUSES = [
    ReservationStatus.UNKNOWN,
    ReservationStatus.C_CONFIRMED,
    ReservationStatus.C_PRELIMINARY,
    ReservationStatus.C_PLANNED,
];

const EMAIL_STATUS = {
    NONE: 0,
    ACCEPTED_AND_REJECTED: 30,
    REJECTED_ONLY: 34,
};

const FSL = {
    OFF: 0,
    INLINE: 1,
    NEXT_TO: 2,
};

const DIVIDER = "__DIVIDER__";

type TAccountProps = { useNewUI: boolean; flags: LDFlagSet } & TAppContextProp;

type TAccountState = {
    langs: { id: string; label: string }[];
    dateFormats: { id: string; label: string }[];
    reservationStatus: Status[];
    timezones: {
        shortName: string;
        name: string;
        offset: string;
    }[];
    /** The user's fields the panel has changed but not yet saved. */
    settings: Partial<User>;
};

class Account extends React.Component<TAccountProps, TAccountState> {
    private _isMounted?: boolean;
    displayName: string;

    constructor(props) {
        super(props);
        this.displayName = "Account";
        this.state = {
            langs: [],
            dateFormats: [],
            reservationStatus: [],
            settings: {},
            timezones: [],
        };
    }

    componentDidMount() {
        this._isMounted = true;
        API.getTimezones((timezones) => {
            if (this._isMounted) {
                this.setState({ timezones });
            }
        });
        API.getLanguages((langs) => {
            if (this._isMounted) {
                langs.unshift({ id: "", label: Language.get("cal_res_side_user_lang_system") });
                this.setState({ langs });
            }
        }, true);
        API.getLanguages((langs) => {
            if (this._isMounted) {
                langs.unshift({ id: "", label: Language.get("cal_res_side_user_lang_system") });
                this.setState({ dateFormats: langs });
            }
        }, false);
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    isRoundedTimeOptionEnabled() {
        return this.props.flags?.coreShowCalendarTimeIndicator;
    }

    isSecondarySizeOptionEnabled() {
        return this.props.flags?.coreShowSecondarySizeOption;
    }

    getString(key) {
        return Language.get(key);
    }

    getValue(field) {
        if (this.state.settings.hasOwnProperty(field)) {
            return this.state.settings[field];
        }

        return this.props.appContext.user?.[field];
    }

    setValue(item, event) {
        if (item.field === "showLists") {
            this.props.appContext.toggleSelection();
        }
        const obj = _.clone(this.state.settings);
        let value;
        if (item.type === "boolean") {
            value = event.target.checked;
        } else if (item.type === "array") {
            value = event;
        } else {
            value = event.target.value;
        }
        if (item.field === "fieldsInSelectionOptions") {
            if (value === FSL.OFF) {
                obj.fieldsInSelection = false;
                obj.fieldsNextToSelection = false;
            }
            if (value === FSL.INLINE) {
                obj.fieldsInSelection = true;
                obj.fieldsNextToSelection = false;
            }
            if (value === FSL.NEXT_TO) {
                obj.fieldsInSelection = true;
                obj.fieldsNextToSelection = true;
            }
        } else {
            obj[item.field] = value;
        }
        this.setState({ settings: obj });
    }

    hasChanges() {
        const user = this.props.appContext.user;
        // eslint-disable-next-line no-restricted-syntax
        for (const prop in user) {
            if (
                user.hasOwnProperty(prop) &&
                this.state.settings[prop] !== undefined &&
                user[prop] !== this.state.settings[prop]
            ) {
                return true;
            }
        }
        return false;
    }

    getFields() {
        const divider = { label: DIVIDER, type: DIVIDER };
        const enableMultiSelect = {
            label: "nc_enable_multi_select",
            field: "enableMultiSelect",
            type: "boolean",
        };
        const timezones =
            this.state.timezones.length > 1
                ? {
                      label: "cal_res_side_user_time_zone",
                      field: "timezone",
                      type: "array",
                      values: this.state.timezones.map((zone) => ({
                          value: zone.shortName,
                          label:
                              zone.name !== zone.shortName
                                  ? `${zone.shortName} ${zone.name}`
                                  : zone.shortName,
                          selected: this.getValue("timezone") === zone.shortName,
                      })),
                  }
                : null;
        const displayTimezones =
            this.state.timezones.length > 1
                ? {
                      label: "nc_other_timezones",
                      field: "displayTimezones",
                      tooltip: "nc_other_timezones_help",
                      multiselect: true,
                      type: "array",
                      values: this.state.timezones.map((zone) => ({
                          value: zone.shortName,
                          label:
                              zone.name !== zone.shortName
                                  ? `${zone.shortName} ${zone.name}`
                                  : zone.shortName,
                          selected: _.contains(this.getValue("displayTimezones"), zone.shortName),
                          disabled: this.getValue("timezone") === zone.shortName,
                      })),
                  }
                : null;
        const useCustomWeekNames =
            this.props.appContext.showEnableCustomWeekNames === true
                ? {
                      label: "nc_use_custom_week_names",
                      field: "useCustomWeekNames",
                      type: "boolean",
                  }
                : null;
        const createGroupsForTracks =
            this.props.appContext.prefsComponentActive === true
                ? {
                      label: "nc_create_groups_for_reservation_tracks",
                      field: "createGroupsForReservationTracks",
                      type: "boolean",
                  }
                : null;
        const promptForGroupName = {
            label: "nc_prompt_for_group_name",
            field: "promptForGroupName",
            type: "boolean",
        };
        const showRoundedTimeIndicator = this.isRoundedTimeOptionEnabled()
            ? {
                  label: "nc_account_indicate_start_time_in_calendar",
                  field: "showRoundedTimeIndicator",
                  type: "boolean",
              }
            : null;
        const useSecondarySize = this.isSecondarySizeOptionEnabled()
            ? {
                  label: "nc_account_use_secondary_size",
                  field: "useSecondarySize",
                  type: "boolean",
              }
            : null;
        const fields = [
            { label: "cal_res_side_user_login", field: "login", disabled: true },
            {
                label: "cal_res_side_user_first_name",
                field: "firstName",
                disabled: true,
            },
            {
                label: "cal_res_side_user_last_name",
                field: "lastName",
                disabled: true,
            },
            {
                label: "cal_res_side_user_email_title",
                field: "email",
                disabled: true,
            },
            {
                label: "nc_cal_res_side_user_request_email",
                field: "emailStatus",
                type: "array",
                values: [
                    {
                        value: EMAIL_STATUS.ACCEPTED_AND_REJECTED,
                        label: Language.get("cal_res_side_user_email_accepted_and_rejected"),
                        selected:
                            this.getValue("emailStatus") === EMAIL_STATUS.ACCEPTED_AND_REJECTED,
                    },
                    {
                        value: EMAIL_STATUS.REJECTED_ONLY,
                        label: Language.get("cal_res_side_user_email_rejected_only"),
                        selected: this.getValue("emailStatus") === EMAIL_STATUS.REJECTED_ONLY,
                    },
                    {
                        value: EMAIL_STATUS.NONE,
                        label: Language.get("cal_res_side_user_email_none"),
                        selected: this.getValue("emailStatus") === EMAIL_STATUS.NONE,
                    },
                ],
            },
            {
                label: "cal_res_side_user_reservation_status_title",
                field: "reservationStatus",
                type: "array",
                values: LISTABLE_STATUSES.map((status) => ({
                    value: status.id,
                    label: status.getStatusName(),
                    selected: this.getValue("reservationStatus") === status.id,
                })),
            },
            divider,
            {
                label: "cal_res_side_user_language",
                field: "language",
                type: "array",
                values: this.state.langs.map((lang) => ({
                    value: lang.id,
                    label: lang.label,
                    selected: this.getValue("language") === lang.id,
                })),
            },
            {
                label: "cal_res_side_user_date_format",
                field: "dateFormat",
                type: "array",
                values: this.state.dateFormats.map((format) => ({
                    value: format.id,
                    label: format.label,
                    selected: this.getValue("dateFormat") === format.id,
                })),
            },
            timezones,
            displayTimezones,
            divider,
            {
                label: "cal_res_side_user_single_click_res",
                field: "allowSingleClickReservation",
                type: "boolean",
            },
            {
                label: "cal_res_side_user_jump_to_next_type",
                field: "jumpToNextType",
                type: "boolean",
            },
            { label: "nc_cal_res_side_user_mute", field: "mute", type: "boolean" },
            {
                label: "nc_show_confirm_entry_removal_warning",
                field: "showConfirmEntryRemovalWarning",
                type: "boolean",
            },
            { label: "menu_view_additional_info", field: "showExtraInfo", type: "boolean" },
            {
                label: "nc_tooltip_in_sidebar",
                field: "tooltipInSidebar",
                type: "boolean",
            },
            {
                label: "menu_view_lists",
                field: "showLists",
                type: "boolean",
            },
            enableMultiSelect,
            {
                label: "nc_reservation_text_size",
                field: "fontSize",
                type: "range",
            },
            createGroupsForTracks,
            promptForGroupName,
            showRoundedTimeIndicator,
            useSecondarySize,
            divider,
            useCustomWeekNames,
            divider,
            {
                label: "nc_fields_in_selection_list_title",
                field: "fieldsInSelectionOptions",
                type: "array",
                values: [
                    {
                        label: Language.get("nc_fields_in_selection_list_off"),
                        value: FSL.OFF,
                        selected:
                            this.getValue("fieldsInSelection") === false &&
                            this.getValue("fieldsNextToSelection") === false,
                    },
                    {
                        label: Language.get("nc_fields_in_selection_list"),
                        value: FSL.INLINE,
                        selected:
                            this.getValue("fieldsInSelection") === true &&
                            this.getValue("fieldsNextToSelection") === false,
                    },
                    {
                        label: Language.get("nc_fields_next_to_selection_list"),
                        value: FSL.NEXT_TO,
                        selected:
                            this.getValue("fieldsInSelection") === true &&
                            this.getValue("fieldsNextToSelection") === true,
                    },
                ],
            },
            divider,
        ];
        return fields;
    }

    abortChanges(event) {
        this.setState({ settings: {} });
        event.preventDefault();
    }

    saveChanges(event) {
        const user = this.props.appContext.user;
        if (!user) {
            event.preventDefault();
            return;
        }
        const obj: Partial<User> = {};
        // eslint-disable-next-line no-restricted-syntax
        for (const prop in user) {
            if (user.hasOwnProperty(prop)) {
                obj[prop] = this.getValue(prop);
            }
        }

        const self = this;
        const reloadPage =
            user.language !== obj.language ||
            user.dateFormat !== obj.dateFormat ||
            user.timezone !== obj.timezone ||
            user.useCustomWeekNames !== obj.useCustomWeekNames ||
            !_.isEqual(user.displayTimezones, obj.displayTimezones);
        const updatedUser = user.immutableSet(obj);
        updatedUser.save((success) => {
            if (!success) {
                Log.warning(Language.get("reset_password_could_not_send_message"));
                return;
            }
            Log.info(Language.get("nc_account_settings_updated."));
            this.props.appContext.update(user, updatedUser);
            self.setState({ settings: {} });
            if (reloadPage) {
                document.location.reload();
            }
        });
        event.preventDefault();
    }

    logout(event) {
        TimeEdit.logoutUser(this.props.appContext.ssoLogout);
        event.preventDefault();
    }

    getUserName() {
        const user = this.props.appContext.user;
        if (!user) {
            return "";
        }
        if (user.firstName || user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        }
        return user.login;
    }

    render() {
        return (
            <div className="accountPane">
                <h2>{this.getUserName()}</h2>

                <form>
                    <ul>
                        {this.getFields().map(this.renderField.bind(this))}
                        <li className="btnGroup horizontal">
                            <button
                                className="deprecated left default cancel"
                                disabled={!this.hasChanges()}
                                onClick={this.abortChanges.bind(this)}
                            >
                                {this.getString("nc_cal_res_side_user_cancel")}
                            </button>
                            <button
                                className="deprecated save"
                                disabled={!this.hasChanges()}
                                onClick={this.saveChanges.bind(this)}
                            >
                                {this.getString("cal_res_side_user_save")}
                            </button>
                        </li>
                    </ul>
                </form>
                {this.props.useNewUI ? null : (
                    <div className="btnGroup horizontal">
                        <button
                            className="deprecated default logout fiftyPercentWide"
                            onClick={this.logout.bind(this)}
                        >
                            {Language.get("nc_menu_log_out")}
                        </button>
                    </div>
                )}
            </div>
        );
    }

    renderField(item, index) {
        if (item === null) {
            return null;
        }
        const onChange = this.setValue.bind(this, item);
        const isDisabled = item.disabled === true;

        if (item.type === DIVIDER) {
            return <li key={index} />;
        }

        if (item.type === "boolean") {
            return (
                <li key={item.field}>
                    <label>
                        <input
                            type="checkbox"
                            checked={this.getValue(item.field)}
                            disabled={isDisabled}
                            onChange={onChange}
                        />
                        <span className={"settingsLabel"}>
                            {item.dontLocalize ? item.label : this.getString(item.label)}
                        </span>
                    </label>
                </li>
            );
        }

        let input = (
            <input
                type={item.type === "password" ? "password" : "text"}
                value={this.getValue(item.field)}
                onChange={onChange}
                disabled={isDisabled}
            />
        );
        if (item.type === "array") {
            if (item.multiselect === true) {
                input = (
                    <MultiSelect
                        className="multiSelect"
                        options={item.values}
                        onValueChanged={onChange}
                    />
                );
            } else {
                input = (
                    <ArrayInput
                        defaultValue={item.values}
                        disabled={isDisabled}
                        onUpdate={onChange}
                        defaultSize={item.defaultSize || 0}
                        limit={item.limit || 1}
                    />
                );
            }
        }

        if (item.type === "range") {
            input = (
                <RangeInput
                    defaultValue={this.getValue(item.field)}
                    onChange={(event) => {
                        const value = parseInt(event.target.value, 10);
                        const user = this.props.appContext.user;
                        if (user) {
                            this.props.appContext.update(user, user.setFontSize(value));
                        }
                        onChange(event);
                    }}
                />
            );
        }

        if (item.type === "array" && item.multiselect === true) {
            return (
                <span key={item.field}>
                    <li key={`${item.field}:label`}>
                        <label
                            title={
                                item.tooltip
                                    ? Language.get(item.tooltip)
                                    : this.getString(item.label)
                            }
                        >
                            <span>
                                {item.dontLocalize ? item.label : this.getString(item.label)}
                            </span>{" "}
                        </label>
                    </li>
                    <li key={`${item.field}:input`}>{input}</li>
                </span>
            );
        }

        return (
            <li key={item.field}>
                <label
                    title={item.tooltip ? Language.get(item.tooltip) : this.getString(item.label)}
                >
                    <span>{item.dontLocalize ? item.label : this.getString(item.label)}</span>{" "}
                    {input}
                </label>
            </li>
        );
    }
}

export default withAppContext(Account);
